// AI-CONTEXT: See docs/features/livereport-widgets-dashboard-parity.md
// One-shot migration for the Activity widget's per-nested settings shape:
// the old RepeaterControl stored an array keyed by item index
// (`settings.repeater[0] = { graphType }`, `[1] = ...`); the new
// NestedItemControl stores a map keyed by stable nested-item ID
// (`settings.byNestedId.outFieldId = { graphType }`).
//
// Index-keyed is fragile: reordering or deleting an output upstream
// silently scrambles which setting belongs to which item. Migration
// projects the array onto IDs using the *current* order of nested
// items, then the widget writes the result back to Firestore on its
// next save. Original `repeater[]` is left untouched so a rollback
// can still read the old shape.

/**
 * @typedef {object} ActivityWidgetSettings
 * @property {Array<Object<string, any>>} [repeater] - Legacy per-nested settings, index-keyed
 * @property {Object<string, Object<string, any>>} [byNestedId] - New per-nested settings, ID-keyed
 */

/**
 * @typedef {object} MigrationResult
 * @property {Object<string, Object<string, any>>} byNestedId - The projected map
 * @property {boolean} migrated - True iff a projection ran on this call (false when byNestedId already existed or repeater was empty)
 */

/**
 * Project an index-keyed `repeater[]` array onto a stable ID-keyed map,
 * using the current order of the activity's nested items.
 *
 * If `settings.byNestedId` already exists (any non-empty object), the
 * existing value is returned as-is and `migrated` is false — the
 * migration is idempotent.
 *
 * Orphan entries (more repeater entries than nested items) are dropped.
 * Missing IDs (a nested item without `ID`) are skipped with a console
 * warning; the matching index in repeater is silently dropped too.
 *
 * @param {ActivityWidgetSettings} settings
 * @param {Array<{ID?: string}>} nestedItems
 * @returns {MigrationResult}
 */
export const migrateActivityRepeater = (settings, nestedItems) => {
    const existing = settings?.byNestedId
    if (existing && typeof existing === 'object' && Object.keys(existing).length > 0) {
        return { byNestedId: existing, migrated: false }
    }

    const repeater = Array.isArray(settings?.repeater) ? settings.repeater : []
    if (repeater.length === 0) {
        return { byNestedId: existing || {}, migrated: false }
    }

    const items = Array.isArray(nestedItems) ? nestedItems : []
    const byNestedId = {}
    let projectionCount = 0

    for (let i = 0; i < items.length; i++) {
        const item = items[i]
        const repeaterEntry = repeater[i]
        if (!repeaterEntry || typeof repeaterEntry !== 'object') continue
        if (!item || !item.ID) {
            console.warn(
                'migrateActivityRepeater: nested item at index',
                i,
                'has no ID — skipping repeater entry',
                repeaterEntry
            )
            continue
        }
        byNestedId[item.ID] = { ...repeaterEntry }
        projectionCount += 1
    }

    if (projectionCount === 0) {
        return { byNestedId: existing || {}, migrated: false }
    }

    return { byNestedId, migrated: true }
}
